import {
    useTotalStaff,
    useTotalHolidays,
    useGetPermissions,
    useUser,
    useExpenses,
    useLeaves,
    useDepartments,
} from "../Use-auth";
import moment from "moment";
import { useDateRange } from "../Pages/DateRangeContext";
import {
    UserRound,
    Volleyball,
    Star,
    AlignStartVertical,
    TrendingUp,
    DollarSign
} from "lucide-react";
import {

    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line
} from "recharts";
import Card from "./Cards";
import ChartCard from "./ChartCard";
import { Link } from "wouter";

const chartConfigs = {
    daily: {
        leaves: {
            title: "Today's Leaves",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Leaves for today"
        },
        payroll: {
            title: "Today' s Payroll",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Payroll for today"
        },
        expenses: {
            title: "Today's Expenses",
            icon: <AlignStartVertical className="w-16 h-16 mx-auto text-orange-500 mb-2" />,
            description: "Expenses for today"
        }
    },
    weekly: {
        leaves: {
            title: "Weekly Leaves",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Leaves this week"
        },
        payroll: {
            title: "Weekly Payroll",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Weekly for today"
        },
        expenses: {
            title: "Weekly Expenses",
            icon: <AlignStartVertical className="w-16 h-16 mx-auto text-orange-500 mb-2" />,
            description: "Expenses this week"
        }
    },
    monthly: {
        leaves: {
            title: "Monthly Leaves",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Leaves this month"
        },
        payroll: {
            title: "Monthly Payroll",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Monthly for today"
        },
        expenses: {
            title: "Monthly Expenses",
            icon: <AlignStartVertical className="w-16 h-16 mx-auto text-orange-500 mb-2" />,
            description: "Expenses this month"
        }
    },
    yearly: {
        leaves: {
            title: "Yearly Leaves",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Leaves this year"
        },
        payroll: {
            title: "Yearly Payroll",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Yearly for today"
        },
        expenses: {
            title: "Yearly Expenses",
            icon: <AlignStartVertical className="w-16 h-16 mx-auto text-orange-500 mb-2" />,
            description: "Expenses this year"
        }
    }
};

export default function HRDashBoard() {
    const { start, end, rangeType } = useDateRange();
    const { data: totalStaff = [] } = useTotalStaff();
    const { data: holidaysData } = useTotalHolidays();
    const { data: existingPermissions } = useGetPermissions();
    const { data: user } = useUser();
    const { data: expensesData, isLoading: fetching } = useExpenses();
    const isHRorAdmin = user?.department === "Admin" || user?.department === "HR";
    const { data: leavesData = [], isLoading } = useLeaves(user?.userId, isHRorAdmin);
    const { data: departments = [] } = useDepartments();

    const isAdmin = user?.department === "Admin";
    const isWithinRange = (date) => {
        if (!start || !end) return true;
        const d = new Date(date);
        return d >= start && d <= end;
    };

    // Permissions
    const currentUserPermissions = isAdmin
        ? { hr: { home_view: true } }
        : existingPermissions?.find((p) => p.userId === user?.userId);

    const canViewHome = isAdmin || currentUserPermissions?.hr?.home_view;

    // Flatten employees
    const allEmployees = totalStaff || [];
    const activeEmployees = allEmployees.filter(
        (e) => e.employeeStatus === "Active"
    );
    // Total leaves

    const totalLeaves = Array.isArray(leavesData)
        ? leavesData.reduce((total, u) => {
            const filtered = u.leaves?.filter((l) => isWithinRange(l.createdAt)) || [];
            return total + filtered.length;
        }, 0)
        : 0;

    const filteredHolidays =
        holidaysData?.holidays?.filter((h) => {
            if (!h.is_active || h.type !== "holiday") return false;

            return isWithinRange(h.date);
        }) || [];

    const filteredEmployees = departments
        .filter(d => d.departmentName !== "Admin")
        .flatMap(d => d.employees || []);

    // Dynamic labels based on rangeType
    let labels = [];
    if (rangeType === "daily") {
        if (rangeType === "daily") labels = ["Today"];
    } else if (rangeType === "weekly") {
        labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    } else if (rangeType === "monthly") {
        const daysInMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
        labels = Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());
    } else if (rangeType === "yearly") {
        labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    }

    const payrollByLabel = labels.map((label, index) => {
        let totalSalary = 0;
        let totalBonus = 0;
        let totalDeductions = 0;
        let totalIncrement = 0;

        filteredEmployees.forEach(emp => {
            emp.payroll?.forEach(p => {

                const date = new Date(
                    p.workInformation?.createdAt ||
                    p.createdAt ||
                    p.updatedAt
                );

                if (start && end) {
                    if (date < start || date > end) return;
                }

                if (rangeType === "daily") {
                    if (date.toDateString() === new Date(start).toDateString()) {
                        totalSalary += p.netSalary || 0;
                        totalBonus += p.earnings?.performanceBonus || 0;
                        totalDeductions += p.deductions?.totalDeductions || 0;
                        totalIncrement += p.earnings?.incrementAmount || 0;
                    }
                }

                else if (rangeType === "weekly") {
                    const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1;

                    if (dayIndex === index) {
                        totalSalary += p.netSalary || 0;
                        totalBonus += p.earnings?.performanceBonus || 0;
                        totalDeductions += p.deductions?.totalDeductions || 0;
                        totalIncrement += p.earnings?.incrementAmount || 0;
                    }
                }

                else if (rangeType === "monthly") {
                    if (date.getDate() === index + 1) {
                        totalSalary += p.netSalary || 0;
                        totalBonus += p.earnings?.performanceBonus || 0;
                        totalDeductions += p.deductions?.totalDeductions || 0;
                        totalIncrement += p.earnings?.incrementAmount || 0;
                    }
                }

                else if (rangeType === "yearly") {
                    if (date.getMonth() === index) {
                        totalSalary += p.netSalary || 0;
                        totalBonus += p.earnings?.performanceBonus || 0;
                        totalDeductions += p.deductions?.totalDeductions || 0;
                        totalIncrement += p.earnings?.incrementAmount || 0;
                    }
                }

            });
        });

        return {
            label,
            totalSalary,
            totalBonus,
            totalDeductions,
            totalIncrement
        };
    });


    // Expenses per label
    const expensesByLabel = labels.map((label, index) => {
        let total = 0;
        (expensesData || []).forEach((report) => {
            (report.expenses || []).forEach((exp) => {
                const d = new Date(exp.expenseDate || exp.createdAt);
                let match = false;

                if (rangeType === "daily") {
                    match = true;
                } else if (rangeType === "weekly") {
                    const dayName = moment(d).format("ddd");
                    match = dayName === label;
                } else if (rangeType === "monthly") {
                    match = d.getDate() === parseInt(label);
                } else if (rangeType === "yearly") {
                    match = d.getMonth() === index;
                }
               
            if (match && isWithinRange(exp.expenseDate || exp.createdAt))
                total += Number(exp.amount) || 0;
            });
        });
        return { label, total };
    });


    const leavesByLabel = labels.map((label, index) => {
        let count = 0;

        leavesData.forEach(user => {
            user.leaves?.forEach(leave => {

                const date = new Date(leave.createdAt);
                if (!isWithinRange(date)) return;

                let match = false;

                if (rangeType === "daily") {
                    match = date.toDateString() === start.toDateString();
                }

                else if (rangeType === "weekly") {
                    const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1;
                    match = dayIndex === index;
                }

                else if (rangeType === "monthly") {
                    match = date.getDate() === index + 1;
                }

                else if (rangeType === "yearly") {
                    match = date.getMonth() === index;
                }

                if (match) count++;
            });
        });

        return { label, leaves: count };
    });

    // Recent expenses
    const allExpenses = (expensesData || [])
    .flatMap((report) =>
        (report.expenses || [])
            .filter((expense) => isWithinRange(expense.expenseDate || expense.createdAt)) 
            .map((expense) => ({
                ...expense,
                reportName: report.report,
                reportId: report._id,
                userName: report.userName,
                userId: report.userId,
            }))
    )
    .sort((a, b) => new Date(b.expenseDate || b.createdAt) - new Date(a.expenseDate || a.createdAt)) 
    .slice(0, 4);

    const currentChart = chartConfigs[rangeType] || chartConfigs.daily;

    const totalExpenseAmount = (expensesData || [])
        .flatMap(report => report.expenses || [])
        .filter(exp => isWithinRange(exp.expenseDate || exp.createdAt))
        .reduce((sum, exp) => {
            const amount = parseFloat(exp.amount);
            return sum + (isNaN(amount) ? 0 : amount);
        }, 0);

    return (
        <div className="min-h-screen p-2 rs:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8">
               <Link href="/myTeam"> <Card title="Total Active Staff" value={canViewHome ? activeEmployees.length : "-"} icon={<UserRound className="w-12 h-12 opacity-80" />} bg="from-blue-500 to-blue-600" /></Link>
                <Link href="/policies"><Card title="National Holidays" value={canViewHome ? filteredHolidays.length : "-"} icon={<Volleyball className="w-12 h-12 opacity-80" />} bg="from-green-500 to-green-600" /></Link>
                 <Link href="/leave-tracking"><Card title="Leaves Request" value={canViewHome ? totalLeaves : "-"} icon={<Star className="w-12 h-12 opacity-80" />} bg="from-purple-500 to-purple-600" /></Link>
                <Card
                    title="Total Expenses"
                    value={
                        canViewHome
                            ? fetching
                                ? "..."
                                : `₹${Number(totalExpenseAmount).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}`
                            : "-"
                    }
                    icon={<AlignStartVertical className="w-12 h-12 opacity-80" />}
                    bg="from-orange-500 to-orange-600"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-8">
                <ChartCard
                    title={currentChart.leaves.title}
                    icon={currentChart.leaves.icon}
                    description={currentChart.leaves.description}
                >
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            data={canViewHome ? leavesByLabel : []}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid stroke="#000" strokeOpacity={0.1} strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#555" }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#555" }} />
                            <Tooltip />
                            <Line type="monotone" dataKey="leaves" stroke="#3b82f6" strokeWidth={2} name="leaves" dot={{ r: 5 }} >
                            </Line>
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard
                    title={currentChart.payroll?.title || "Payroll"}
                    icon={currentChart.payroll?.icon}
                    description={currentChart.payroll?.description || ""}
                >
                    {canViewHome && filteredEmployees.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart
                                data={payrollByLabel}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid stroke="#000" strokeOpacity={0.1} strokeDasharray="3 3" />
                                <XAxis yAxisId="left" dataKey="label" tick={{ fontSize: 12, fill: "#555" }} />
                                <YAxis yAxisId="right" tick={{ fontSize: 12, fill: "#555" }} />
                                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                                <Line yAxisId="left" type="monotone" dataKey="totalSalary" stroke="#3b82f6" strokeWidth={2} name="Salary" />
                                <Line yAxisId="right" type="monotone" dataKey="totalBonus" stroke="#10b981" strokeWidth={2} name="Bonus" />
                                <Line yAxisId="right" type="monotone" dataKey="totalDeductions" stroke="#ef4444" strokeWidth={2} name="Deductions" />
                                <Line yAxisId="right" type="monotone" dataKey="totalIncrement" stroke="#8b5cf6" strokeWidth={2} name="Increment" />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="text-center text-gray-400 mt-4">No payroll data available</p>
                    )}
                </ChartCard>
                <ChartCard
                    title={currentChart.expenses.title}
                    icon={currentChart.expenses.icon}
                    description={currentChart.expenses.description}
                >
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={canViewHome ? expensesByLabel : []} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid stroke="#000" strokeOpacity={0.1} strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#555" }} />
                            <YAxis tick={{ fontSize: 12, fill: "#555" }} />
                            <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                            <Line type="monotone" dataKey="total" stroke="#F97316" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-3 sm:gap-6">
                <div className="bg-white rounded-2xl  p-3 sm:p-6 shadow-lg border border-gray-100">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-[14px] sm:text-lg xl:text-xl text-gray-800">Leave Requests</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-gray-600 border-b border-gray-200 ">
                                <tr className="text-xs sm:text-[14px]">
                                    <th className="text-left px-1 sm:px-2 py-2">Employee ID</th>
                                    <th className="text-left px-1 sm:px-2 py-2 hidden rs:table-cell">User Name</th>
                                    <th className="px-1 sm:px-2 py-2 text-left">Type</th>
                                    <th className="text-left px-1 sm:px-2 py-2 hidden xs:table-cell">Date</th>
                                    <th className="text-left px-1 sm:px-2 py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(canViewHome ? leavesData : []).flatMap((userLeaves) =>
                                    (userLeaves.leaves || [])
                                        .filter((leave) => isWithinRange(leave.createdAt))
                                        .map((leave) => ({
                                            _id: leave._id,
                                            userId: userLeaves.userId,
                                            userName: userLeaves.userName,
                                            userEmail: userLeaves.userEmail,
                                            department: userLeaves.department,
                                            subject: leave.subject || leave.type,
                                            type: leave.type,
                                            startDate: leave.startDate,
                                            endDate: leave.endDate,
                                            status: leave.status || "-",
                                            createdAt: leave.createdAt
                                        }))
                                ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                    .slice(0, 4)
                                    .map((request, i) => (
                                        <tr key={i} className="hover:bg-gray-50 transition-colors text-xs sm:text-[14px]">
                                            <td className="px-1 sm:px-2 py-2">{request.userId}</td>
                                            <td className="px-1 sm:px-2 py-2 font-medium truncate whitespace-nowrap hidden rs:table-cell">{request.userName}</td>
                                            <td className="px-1 sm:px-2 py-2 font-medium truncate whitespace-nowrap">{request.type}</td>
                                            <td className="px-1 sm:px-2 py-2 text-left truncate whitespace-nowrap hidden xs:table-cell">
                                                {request.startDate
                                                    ? new Date(request.startDate).toLocaleDateString() +
                                                    (request.endDate && request.endDate !== request.startDate
                                                        ? " - " + new Date(request.endDate).toLocaleDateString()
                                                        : "")
                                                    : "-"}
                                            </td>
                                            <td className="px-1 sm:px-4 py-2 text-left truncate whitespace-nowrap">
                                                <span
                                                    className={`px-1 py-1 rounded-full text-xs font-medium ${request.status === "Approved"
                                                        ? "bg-green-100 text-green-800"
                                                        : request.status === "Pending"
                                                            ? "bg-yellow-100 text-yellow-800"
                                                            : "bg-red-100 text-red-800"
                                                        }`}
                                                >
                                                    {request.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="bg-white rounded-2xl  p-3 sm:p-6 shadow-lg border border-gray-100">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-[14px] sm:text-lg xl:text-xl text-gray-800">Expenses History</h3>
                        {/* <EllipsisVertical className="w-6 h-6 text-gray-400 cursor-pointer" /> */}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm table-fixed">
                            <thead className="text-gray-600 border-b-2 border-gray-200 text-left">
                                <tr className="text-xs sm:text-[14px]">
                                    <th className="px-1 sm:px-2 py-2 ">S/N</th>
                                    <th className="px-1 sm:px-2 py-2 hidden rs:table-cell">MERCHANT</th>
                                    <th className="px-1 sm:px-2 py-2 hidden zy:table-cell">AMOUNT</th>
                                    <th className="px-1 sm:px-2 py-2 ">REPORT NAME</th>
                                    <th className="px-1 sm:px-2 py-2 hidden rs:table-cell">Date</th>
                                    <th className="px-1 sm:px-2 py-2 ">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {canViewHome && allExpenses.map((expense, index) => (
                                    <tr key={expense._id} className="hover:bg-gray-50 text-xs sm:text-sm">
                                        <td className="px-1 sm:px-2 py-2">{index + 1}</td>
                                        <td className="px-1 sm:px-2 py-2 truncate whitespace-nowrap font-medium hidden rs:table-cell">{expense.merchant}</td>
                                        <td className="px-1 sm:px-2 py-2 truncate whitespace-nowrap hidden zy:table-cell">₹{expense.amount}</td>
                                        <td className="px-1 sm:px-2 py-2 truncate whitespace-nowrap">{expense.reportName}</td>
                                        <td className="px-1 sm:px-2 py-2 hidden rs:table-cell">
                                            <p className="text-gray-800">{new Date(expense.expenseDate).toLocaleDateString("en-GB")}</p>
                                        </td>
                                        <td className="px-1 sm:px-2 py-2 truncate whitespace-nowrap font-medium">
                                            <span
                                                className={`px-1 py-1 text-xs rounded-md font-medium ${expense.status === "Paid"
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-purple-100 text-purple-700"
                                                    }`}
                                            >
                                                {expense.status === "Paid" ? "Paid" : "UnPaid"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
